import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp, Phone } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { SITE } from '../../data/site';

// Scroll to the very top instantly on every route change so new pages
// always open at the top rather than retaining the previous scroll position.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 32 32" fill="currentColor" {...props}>
    <path d="M19.11 17.2c-.27-.14-1.6-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.17-1.34-.8-.71-1.34-1.6-1.5-1.87-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29 0 1.35.98 2.66 1.12 2.84.14.18 1.93 2.95 4.68 4.13.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.32z"/>
    <path d="M16.02 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.1 28.8l6.56-1.72a12.74 12.74 0 0 0 6.35 1.62h.01c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.33-6.64-3.75-9.06A12.71 12.71 0 0 0 16.02 3.2zm0 23.4h-.01c-1.94 0-3.84-.52-5.5-1.51l-.39-.23-3.89 1.02 1.04-3.79-.26-.4a10.6 10.6 0 0 1-1.62-5.66c0-5.87 4.78-10.65 10.65-10.65 2.85 0 5.52 1.11 7.53 3.12a10.57 10.57 0 0 1 3.12 7.53c0 5.87-4.78 10.57-10.62 10.57z"/>
  </svg>
);

export const Layout = ({ children }) => {
  const [showTop, setShowTop] = useState(false);

  // Subscribe once on mount. `onScroll` is defined inside the effect and
  // `setShowTop` from useState is guaranteed stable by React, so an empty
  // dependency array is correct here (no stale closures are possible).
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />

      {/* Floating action buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3">
        {showTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-11 h-11 rounded-full bg-background shadow-card border border-border flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
            aria-label="Back to top"
            data-testid="back-to-top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        )}
        <a
          href={SITE.whatsapp}
          target="_blank"
          rel="noreferrer"
          aria-label="Chat on WhatsApp"
          data-testid="whatsapp-button"
          className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-card hover:scale-105 transition-transform duration-200"
        >
          <WhatsAppIcon className="w-8 h-8" />
        </a>
      </div>

      <a
        href={SITE.phoneHref}
        className="fixed bottom-6 left-6 z-50 lg:hidden flex items-center gap-2 bg-primary text-primary-foreground font-heading font-medium px-5 py-3 rounded-full shadow-card"
        data-testid="mobile-call-now"
      >
        <Phone className="w-4 h-4" /> Call now
      </a>
    </div>
  );
};
